// pageTransition.subtle.js
import barba from "@barba/core";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { initLenis } from "./initLenis.js";

gsap.registerPlugin(ScrollTrigger);

if ("scrollRestoration" in history) history.scrollRestoration = "manual";
window.scrollTo(0, 0);

/* -----------------------------------------
   CONFIG
----------------------------------------- */
const CONFIG = {
  // Duración base
  duration: 0.6,

  // Easing
  easeOut: "power2.out",
  easeIn: "power2.in",

  // Movimiento sutil (px)
  yOffset: 40,

  // Blur durante transición
  blur: 6,

  // Stagger entre elementos
  stagger: 0.04,

  // Selector de elementos a animar (dentro del container)
  // Deja vacío "" para animar solo el container
  animateSelector:
    "section, header, .animate-in, h1, h2:not(.marquee-scroll__text), p, img, .card",
};

/* -----------------------------------------
   Obtener elementos animables
----------------------------------------- */
function getAnimatableElements(container) {
  if (!CONFIG.animateSelector) return [container];

  const elements = container.querySelectorAll(CONFIG.animateSelector);
  if (!elements.length) return [container];

  return Array.from(elements).filter((el) => {
    // Excluir si tiene data-barba-animate="false" o está dentro de uno
    if (el.closest('[data-barba-animate="false"]')) return false;

    // Solo los que están en viewport
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  });
}
/* -----------------------------------------
   GSAP Cleanup
----------------------------------------- */
function killGsapIn(container) {
  if (!container) return;
  try {
    gsap.killTweensOf(container);
    gsap.killTweensOf(container.querySelectorAll("*"));
  } catch {}
  ScrollTrigger.getAll().forEach((st) => {
    if (st.trigger && container.contains(st.trigger)) st.kill(true);
  });
}

/* -----------------------------------------
   Init
----------------------------------------- */
export function initBarbaTransitions({
  common = [],
  byNs = {},
  initOnLoad = true,
} = {}) {
  if (window.__BARBA_SUBTLE__) return;
  window.__BARBA_SUBTLE__ = true;

  let lenis = null;
  let destroyers = [];
  const runInitsFor = (container) => {
    destroyers.forEach((d) => {
      try {
        d?.();
      } catch {}
    });
    destroyers = [];

    const ns = container?.getAttribute("data-barba-namespace") || "default";
    const fns = [...(byNs[ns] || []), ...common];

    requestAnimationFrame(() => {
      fns.forEach((fn) => {
        if (typeof fn !== "function") return;
        try {
          const res = fn(container);
          if (typeof res === "function") destroyers.push(res);
        } catch (e) {
          console.error("[Barba] init error:", e);
        }
      });
      // NO refresh aquí
    });
  };
  if (initOnLoad) {
    window.addEventListener(
      "load",
      () => {
        lenis = initLenis();
        if (lenis) {
          lenis.scrollTo(0, { immediate: true });
        } else {
          window.scrollTo(0, 0);
        }

        const container =
          document.querySelector('[data-barba="container"]') || document;
        runInitsFor(container);
      },
      { once: true },
    );
  }

  barba.init({
    prevent: ({ el, current, next }) => {
      const href = el?.getAttribute?.("href") || "";
      if (!href || href.startsWith("#")) return true;
      if (el.hasAttribute("target") || el.hasAttribute("download")) return true;
      if (el.closest("[data-barba-prevent]")) return true;

      const norm = (p) => (p || "").replace(/\/+$/, "") || "/";
      return (
        current?.url?.path &&
        next?.url?.path &&
        norm(current.url.path) === norm(next.url.path)
      );
    },

    transitions: [
      {
        name: "subtle-fade",

        once({ next }) {
          const c = next.container;
          const elements = getAnimatableElements(c);

          gsap.set(elements, {
            autoAlpha: 0,
            y: CONFIG.yOffset * 0.5,
          });

          return gsap.to(elements, {
            autoAlpha: 1,
            y: 0,
            duration: CONFIG.duration,
            ease: CONFIG.easeOut,
            stagger: CONFIG.stagger,
          });
        },

        async leave({ current }) {
          const c = current.container;
          c.style.pointerEvents = "none";
          killGsapIn(c);

          const elements = getAnimatableElements(c);

          await gsap.to(elements, {
            autoAlpha: 0,
            y: -CONFIG.yOffset * 0.5,
            filter: `blur(${CONFIG.blur}px)`,
            duration: CONFIG.duration * 0.7,
            ease: CONFIG.easeIn,
            stagger: {
              each: CONFIG.stagger * 0.5,
              from: "start",
            },
          });

          c.style.display = "none";
        },

        beforeEnter({ next }) {
          const c = next.container;
          c.style.pointerEvents = "none";

          if (!lenis) lenis = initLenis();
          if (lenis) {
            lenis.scrollTo(0, { immediate: true });
          } else {
            window.scrollTo(0, 0);
          }

          killGsapIn(c);
          runInitsFor(c);

          const elements = getAnimatableElements(c);
          gsap.set(c, { autoAlpha: 1 });
          gsap.set(elements, {
            autoAlpha: 0,
            y: CONFIG.yOffset,
            filter: `blur(${CONFIG.blur}px)`,
          });
        },

        async enter({ next }) {
          const c = next.container;
          const elements = getAnimatableElements(c);

          await gsap.to(elements, {
            autoAlpha: 1,
            y: 0,
            filter: "blur(0px)",
            duration: CONFIG.duration,
            ease: CONFIG.easeOut,
            stagger: {
              each: CONFIG.stagger,
              from: "start",
            },
          });

          elements.forEach((el) => {
            el.style.filter = "";
          });

          c.style.pointerEvents = "";
          // NO refresh aquí
        },
      },
    ],
  });

  barba.hooks.after(() => {
    setTimeout(() => {
      ScrollTrigger.refresh(true);
    }, 150);
  });
}
